import SendDetails from '@modules/SendDetails'
import { Fee, SendMode } from '@plug-core/hooks/useSend'
import useSendReview from '@plug-core/hooks/useSendReview'
import { NFT } from '@plug-core/interfaces/common'
import { UTXO } from '@plug-core/interfaces/walletActions'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { Button, Heading } from '@ui'
import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  toAddress: string
  token?: TokenBalance
  amount: string
  nft?: NFT
  cancelAction: () => void
  /** Called as soon as the transfer lands; the parent closes the sheet and leaves. */
  onSuccess: () => void
  onError: (error: string) => void
  xtcSendMode?: string
  utxos: UTXO[]
  fee?: Fee
  mode?: SendMode
}

const SendReview: FC<Props> = ({
  toAddress,
  token,
  amount,
  cancelAction,
  onSuccess,
  nft,
  onError,
  xtcSendMode,
  utxos,
  fee,
  mode,
}) => {
  const { sendSuccess, sendInProgress, performPay, warnings, knownContact, isReady } =
    useSendReview({
      amount,
      token,
      nft,
      toAddress,
      fee,
      utxos,
      xtcSendMode,
      onError,
      // The hook goes back 3s after success (it used to show a success animation meanwhile).
      // Leaving is handled right away by onSuccess, so only its balance refresh still matters.
      goBack: () => {},
      mode,
    })

  const { t } = useTranslation()

  const action = mode === SendMode.Send ? t('send.title') : t('send.canisterTopup')

  useEffect(() => {
    if (!sendSuccess) return
    onSuccess()
  }, [sendSuccess])

  // Stays busy while the sheet slides out after success.
  const busy = sendInProgress || sendSuccess

  return (
    <div className="flex flex-col gap-4">
      <Heading level={4} align="center">
        {t('send.review.title', { action })}
      </Heading>

      <SendDetails
        toAddress={toAddress}
        nft={nft}
        token={token}
        amount={amount}
        warnings={warnings}
        knownContact={knownContact}
      />

      <div className="mt-2 flex flex-col gap-4">
        <Button
          variant="primary"
          loading={busy || !isReady}
          disabled={busy || !isReady}
          onClick={performPay}
          fullWidth
        >
          {isReady ? action : t('send.review.checkingReceiver')}
        </Button>

        <Button variant="ghost" onClick={cancelAction} disabled={busy} fullWidth>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

export default SendReview
